import axios from 'axios';
import { getAuthHeaders } from './auth';

const API_BASE = '/api';

export interface Document {
  id: number;
  title: string;
  language: string;
  effective_date?: string;
  source_url?: string;
  status: string;
  created_at: string;
  updated_at?: string;
}

// Per-file classification result from the knowledge-unit upload endpoint.
export interface UploadFileResult {
  filename: string;
  status: 'unit' | 'source_page' | 'skipped' | 'error';
  stable_id?: string;
  error?: string;
}

export interface UploadResponse {
  files: UploadFileResult[];
  units_created: number;
  units_updated: number;
  documents_created: number;
  results: { stable_id?: string; document?: string; action: string; error?: string }[];
  total: number;
}

// Knowledge-unit ingestion: each frontmatter .md = exactly ONE chunk (upserted
// by stable id); plain .md files are source pages for citations. This is the
// same pipeline as the bulk corpus ingest — the legacy auto-chunking upload
// is gone.
export const uploadDocumentFiles = async (
  files: File[], domainId?: number
): Promise<UploadResponse> => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  if (domainId) formData.append('domain_id', String(domainId));
  const response = await axios.post(`${API_BASE}/admin/documents/upload`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const getDocuments = async (statusFilter?: string): Promise<Document[]> => {
  const params = statusFilter ? { status_filter: statusFilter } : {};
  const response = await axios.get(`${API_BASE}/admin/documents`, {
    headers: getAuthHeaders(),
    params
  });
  return response.data;
};

export const getDocument = async (id: number): Promise<Document> => {
  const response = await axios.get(`${API_BASE}/admin/documents/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export interface DocumentDetail extends Document {
  raw_text: string | null;
  version: number;
  domain_id?: number;
  domain_name?: string;
  chunks: {
    id: number;
    index: number;
    text: string;
    context_header: string | null;
    start_offset: number | null;
    end_offset: number | null;
    has_embedding: boolean;
  }[];
}

export const getDocumentDetail = async (id: number): Promise<DocumentDetail> => {
  const response = await axios.get(`${API_BASE}/admin/documents/${id}/detail`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateDocumentStatus = async (id: number, status: string) => {
  const response = await axios.put(
    `${API_BASE}/admin/documents/${id}/status`,
    null,
    {
      headers: getAuthHeaders(),
      params: { status }
    }
  );
  return response.data;
};

export const ingestDocument = async (id: number) => {
  const response = await axios.post(
    `${API_BASE}/admin/documents/${id}/ingest`,
    null,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteDocument = async (id: number) => {
  const response = await axios.delete(
    `${API_BASE}/admin/documents/${id}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const testRetrieval = async (query: string, language?: string) => {
  const params = language ? { query, language } : { query };
  const response = await axios.post(
    `${API_BASE}/admin/retrieval/test`,
    null,
    {
      headers: getAuthHeaders(),
      params
    }
  );
  return response.data;
};

// Simple-form knowledge unit — no .md, no YAML; server generates slugs and
// the source document. Same pipeline as file upload underneath.
export interface KnowledgeUnitForm {
  language: 'ar' | 'en';
  document_title: string;
  title: string;
  body: string;
  questions: string[];
  tags?: string[];
  domain_id?: number;
}

export interface KnowledgeUnitResult {
  stable_id: string;
  document: string;
  action: 'created' | 'updated';
}

export const createKnowledgeUnit = async (form: KnowledgeUnitForm): Promise<KnowledgeUnitResult> => {
  const res = await axios.post(`${API_BASE}/admin/knowledge-units`, form, { headers: getAuthHeaders() });
  return res.data;
};

// Replace a document's SOURCE PAGE — the full original text citations open
// at /doc/{id}. Display-only (never embedded), stored as a new version.
export const updateSourcePage = async (documentId: number, text: string): Promise<{ version: number }> => {
  const res = await axios.put(`${API_BASE}/admin/documents/${documentId}/source-page`, { text }, { headers: getAuthHeaders() });
  return res.data;
};
